// src/components/useAuth.js
import { useState, useEffect, useContext, createContext } from "react";
import {
  getAuth,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  signInWithPhoneNumber,
  RecaptchaVerifier,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";

// Create a context for authentication
const AuthContext = createContext(null);

// Provider component that wraps your app and makes auth object available
export function AuthProvider({ children }) {
  const auth = useProvideAuth();
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
}

// Hook for components to get auth object
export const useAuth = () => {
  return useContext(AuthContext);
};

// Provider hook that creates auth object and handles state
function useProvideAuth() {
  const [user, setUser] = useState(null);
  const [firebaseUser, setFirebaseUser] = useState(null); // Full Firebase user object
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const auth = getAuth();

  // Get Firebase ID token
  const getIdToken = async (forceRefresh = false) => {
    if (!firebaseUser) {
      throw new Error("No authenticated user");
    }

    try {
      return await firebaseUser.getIdToken(forceRefresh);
    } catch (err) {
      console.error("Error getting ID token:", err);
      setError(err.message);
      throw err;
    }
  };

  // Sign in with Google
  const loginWithGoogle = async () => {
    setLoading(true);
    setError(null);
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope("openid");
      provider.addScope("email");
      const result = await signInWithPopup(auth, provider);

      // Store user data in sessionStorage for persistence
      sessionStorage.setItem(
        "authUser",
        JSON.stringify({
          uid: result.user.uid,
          email: result.user.email,
          displayName: result.user.displayName,
          photoURL: result.user.photoURL,
        })
      );

      return result.user;
    } catch (err) {
      console.error("Google sign-in error:", err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Sign in with Apple
  const loginWithApple = async () => {
    setLoading(true);
    setError(null);
    try {
      const provider = new OAuthProvider("apple.com");
      const result = await signInWithPopup(auth, provider);

      // Store user data in sessionStorage
      sessionStorage.setItem(
        "authUser",
        JSON.stringify({
          uid: result.user.uid,
          email: result.user.email,
          displayName: result.user.displayName,
          photoURL: result.user.photoURL,
        })
      );

      return result.user;
    } catch (err) {
      console.error("Apple sign-in error:", err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const setupRecaptcha = (elementOrId) => {
    try {
      // Check if auth is properly initialized
      if (!auth) {
        console.error("Firebase auth not initialized");
        throw new Error("Firebase auth not initialized");
      }

      // Handle both element references and string IDs
      const container =
        typeof elementOrId === "string"
          ? document.getElementById(elementOrId)
          : elementOrId;

      if (!container) {
        throw new Error(`Recaptcha container not found: ${elementOrId}`);
      }

      // Clear any existing instance
      if (window.recaptchaVerifier) {
        try {
          window.recaptchaVerifier.clear();
        } catch (e) {
          console.warn("Failed to clear existing recaptcha:", e);
        }
      }

      // Create a new instance with proper error handling
      window.recaptchaVerifier = new RecaptchaVerifier(
        "recaptcha-container",
        {
          size: "invisible",
          callback: () => {
            console.log("reCAPTCHA solved");
          },
          "expired-callback": () => {
            console.log("reCAPTCHA expired");
          },
        },
        auth
      );

      return window.recaptchaVerifier;
    } catch (error) {
      console.error("Error setting up recaptcha:", error);
      throw error;
    }
  };
  // Send SMS verification code
  const sendSmsCode = async (phoneNumber, recaptchaElementOrId) => {
    setLoading(true);
    setError(null);
    try {
      const appVerifier = setupRecaptcha(recaptchaElementOrId);
      const confirmationResult = await signInWithPhoneNumber(
        auth,
        phoneNumber,
        appVerifier
      );
      window.confirmationResult = confirmationResult;
      return true;
    } catch (err) {
      console.error("SMS send error:", err);
      setError(err.message);
      if (window.recaptchaVerifier) {
        window.recaptchaVerifier.clear();
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Verify SMS code
  const verifySmsCode = async (code) => {
    setLoading(true);
    setError(null);
    try {
      const result = await window.confirmationResult.confirm(code);

      // Store user data in sessionStorage
      sessionStorage.setItem(
        "authUser",
        JSON.stringify({
          uid: result.user.uid,
          phoneNumber: result.user.phoneNumber,
        })
      );

      return result.user;
    } catch (err) {
      console.error("SMS verification error:", err);
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Sign out
  const logout = async () => {
    try {
      await signOut(auth);
      // Clear user from sessionStorage
      sessionStorage.removeItem("authUser");
      setUser(null);
    } catch (err) {
      console.error("Sign out error:", err);
      setError(err.message);
    }
  };

  // Reset recaptcha
  const resetRecaptcha = () => {
    if (window.recaptchaVerifier) {
      window.recaptchaVerifier.clear();
      window.recaptchaVerifier = null;
    }
  };

  // Subscribe to auth state changes and check sessionStorage on init
  useEffect(() => {
    // First, check sessionStorage for existing auth
    // const storedUser = sessionStorage.getItem("authUser");
    // if (storedUser) {
    //   setUser(JSON.parse(storedUser));
    // }

    // Then subscribe to Firebase auth changes
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setFirebaseUser(user);

        // Store minimal user data in state and sessionStorage
        const userData = {
          uid: user.uid,
          email: user.email,
          phoneNumber: user.phoneNumber,
          displayName: user.displayName,
          photoURL: user.photoURL,
        };
        setUser(userData);
        sessionStorage.setItem("authUser", JSON.stringify(userData));
      } else {
        setUser(null);
        setFirebaseUser(null);

        // Don't clear sessionStorage here - we only want to do that on explicit logout
      }
      setLoading(false);
    });

    // Cleanup subscription
    return () => unsubscribe();
  }, [auth]);

  return {
    user,
    firebaseUser,
    getIdToken,
    loading,
    error,
    loginWithGoogle,
    loginWithApple,
    sendSmsCode,
    verifySmsCode,
    logout,
    resetRecaptcha,
  };
}
